/* eslint-disable no-bitwise */
/* ~~(foo) is equivalent to Math.floor(foo) but sometimes faster */
import type { PeakRange } from '@mimir/web/model/audioTrackConfig';

async function downloadAudio(url: string, index: number, progressCallback: (index: number, progress: number) => void): Promise<ArrayBuffer | null> {
  return new Promise<ArrayBuffer | null>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.open('GET', url, true);
    xhr.responseType = 'arraybuffer';

    xhr.addEventListener('progress', (e) => {
      const progress = e.loaded / e.total;
      progressCallback(index, progress);
    });

    xhr.addEventListener('load', _e => {
      if (xhr.status === 200) {
        resolve(xhr.response as ArrayBuffer);
      } else if (xhr.status === 404 || xhr.status === 400) {
        // If we get a 404 (or 400 since the S3 server may return 400),
        // let's return null to indicate that the file does not exist
        resolve(null);
      } else {
        reject(new Error(`Audio channel: ${url} failed to download. Msg: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error(`Audio channel: ${url} failed to download`));
    });

    xhr.send();
  });
}

export const downloadAll = (urls: string[], totalProgressCallback: (progress: number) => void): Promise<(ArrayBuffer | null)[]> => {
  const tp = Array.from<number>({ length: urls.length });
  const progressCallback = (index: number, progress: number) => {
    tp[index] = progress;
    const total = tp.reduce((p: number, cur: number) => p += cur, 0);
    totalProgressCallback(total / urls.length);
  };
  return new Promise<(ArrayBuffer | null)[]>((resolve, reject) => {
    try {
      const downloadJobs: Promise<ArrayBuffer | null>[] = [];
      urls.forEach((url: string, index: number) => {
        tp[index] = 0;
        downloadJobs.push(downloadAudio(url, index, progressCallback));
      });
      Promise.all(downloadJobs).then((res: (ArrayBuffer | null)[]) => {
        resolve(res);
      }).catch((error) => {
        reject(error);
      });
    } catch (error) {
      reject(error);
    }
  });
};

export async function decodeAudio(audioContext: AudioContext, arrayBuffer: ArrayBuffer): Promise<AudioBuffer | null> {
  if (arrayBuffer.byteLength === 0) {
    return null;
  }
  return new Promise<AudioBuffer>((resolve, reject) => {
    /*
      The "void" modifier is specified here to indicate that we're using the old
      variant of decodeAudioData() (to support Safari) rather than the newer one
      that returns a promise. I.e. we expect errors to be propagated in the
      error callback, rather than in a returned promise.
    */
    void audioContext.decodeAudioData(arrayBuffer, buffer => resolve(buffer), err => reject(err));
  });
}

/**
 * Linear interpolate from a to b over f = [0.0, 1.0]
 */
const lerp = (a: number, b: number, f: number) => {
  return (a * (1.0 - f)) + (b * f);
};

/**
 *  Combine peak ranges. The second range interval values are relative to the
 *  first range.
 */
export function combinePeakRanges(first: PeakRange, second: PeakRange): PeakRange {

  return {
    start: lerp(first.start, first.end, second.start),
    end: lerp(first.start, first.end, second.end),
  };
}

export function getPeaks(
  fullBuffer: Float32Array,
  width: number,
  range: PeakRange,
): number[] {
  const fullSize = fullBuffer.length;
  const { start, end } = range;
  const buffer =
    fullBuffer.slice(Math.round(start * fullSize), Math.round(end * fullSize));

  const sampleSize = buffer.length / width;
  const sampleStep = ~~(sampleSize / 10) || 1;
  const p = [];
  for (let i = 0; i < width; i++) {
    const start = ~~(i * sampleSize);
    const end = ~~(start + sampleSize);
    let max = 0;
    for (let j: number = start; j < end; j += sampleStep) {
      const value = buffer[j];
      if (value > max) {
        max = value;
      } else if (-value > max) {
        max = -value;
      }
    }
    p[i] = max;
  }
  return p;
}


/**
 * Unlike getPeaks() that operates on a Float32Array that contains all the audio samples,
 * this function operates on a Uint8Array that contains the currently available peak values.
 * Since the actual media duration may be larger than the media duration we have peaks
 * for, we use peakBufferVirtualSize instead of the buffer size, since peakBufferVirtualSize
 * represents the peak count we would have if we had peaks for the whole media duration.
 */
export function getPeaksFromUint8Array(
  peakBuffer: Uint8Array,
  peakBufferVirtualSize: number,
  width: number,
  range: PeakRange,
): number[] {
  const fullSize = peakBufferVirtualSize;
  const { start, end } = range;
  const bufferStart = Math.round(start * fullSize);
  const bufferEnd = Math.round(end * fullSize);
  const buffer = peakBuffer.slice(Math.round(start * fullSize), Math.round(end * fullSize));
  const bufferVirtualLength = bufferEnd - bufferStart;
  const sampleSize = bufferVirtualLength / width;
  const sampleStep = ~~(sampleSize / 10) || 1;
  const p = [];
  for (let i = 0; i < width; i++) {
    const start = ~~(i * sampleSize);
    let end = ~~(start + sampleSize);
    if (end === start) {
      // Even if we zoom very far, we want to consider at least one peak
      // per pixel to draw
      end++;
    }
    let max = 0;
    for (let j: number = start; j < end; j += sampleStep) {
      // Since the actual buffer might be shorter than peakBufferVirtualSize,
      // let's pretend we have a peak value of 0 if we are outside
      // the available peak buffer
      const value = j >= bufferVirtualLength ? 0 : buffer[j];
      if (value > max) {
        max = value;
      }
    }
    p[i] = max / 255;
  }
  return p;
}

export const drawPeaks = (peaks: number[], ctx: CanvasRenderingContext2D, y: number, width: number, height: number, scale: number = 1) => {
  if (peaks.length === 0) {
    return;
  }
  const halvPixel = 0.5 / window.devicePixelRatio;
  const length = peaks.length;
  const max = 1;
  ctx.fillStyle = '#006992';
  ctx.strokeStyle = '#006992';
  ctx.beginPath();
  ctx.moveTo(halvPixel, height + y);
  for (let i = 0; i < length; i++) {
    const h = Math.round(peaks[i] / max * height);
    ctx.lineTo(i * scale + halvPixel, (height - h) + y);
  }
  ctx.lineTo(width + halvPixel, height + y);
  ctx.closePath();
  ctx.fill();
  ctx.fillRect(0, (height - halvPixel) + y, width, halvPixel);
  ctx.stroke();
};

export const getHDPContext = (
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  devicePixelRatio: number,
): CanvasRenderingContext2D => {
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
  canvas.width = width * devicePixelRatio;
  canvas.height = height * devicePixelRatio;
  canvas.style.width = width + 'px';
  canvas.style.height = height + 'px';
  if (devicePixelRatio > 1) {
    ctx.scale(devicePixelRatio, devicePixelRatio);
  }
  return ctx;
};
